import React, { useEffect, useId, useRef, useState } from 'react'
import RcSelect, { BaseSelectRef, SelectProps as RcSelectProps } from 'rc-select'
import { InputRef } from 'rc-input'
import { IconRegularChevronDown } from '@notino/react-styleguide'
import { useIntl } from 'react-intl'

// types
import { BaseSelectOption, SelectOptionValue, SelectProps, SelectValueType } from './types'

// styles
import * as SC from './SelectStyles'
import { BREAKPOINTS_INTS, SELECT_DROPDOWN_CLASS_NAME } from '../../styles/constants'

// assets
import CloseIcon from '../../assets/icons/CloseIcon'
import CheckIcon from '../../assets/icons/CheckIcon'

// components
import Drawer from '../../components/Drawer/Drawer'
import ErrorScreen from '../ErrorScreen/ErrorScreen'
import Empty from '../Empty/Empty'
import OverlayLoading from '../OverlayLoading/OverlayLoading'
import InputLabel from '../InputLabel/InputLabel'
import InputTrigger from '../InputTrigger/InputTrigger'

// hooks
import useIsMobile from '../../hooks/useIsMobile'
import useMessages from '../../hooks/useMessages'

// utils
import { transformToLowerCaseWithoutAccent } from '../../utils/helper'

const STATUS_ANNOUNCE_DEBOUNCE_MS = 300
const RESULTS_AVAILABLE_MESSAGE_ID = '{count, plural, one {# result available} other {# results available}}'

const getIsSelected = <ValuePropsType extends SelectValueType = string, OptionPropsType extends BaseSelectOption = BaseSelectOption>(
	value: ValuePropsType,
	option: OptionPropsType
) => {
	if (!value) {
		return false
	}

	if (typeof value === 'string' || typeof value === 'number') {
		return value === option.value
	}

	if ('value' in value) {
		return value.value === option.value
	}

	if (Array.isArray(value)) {
		return !!value.find((val) => {
			if (typeof val === 'string' || typeof val === 'number') {
				return val === option.value
			}

			if ('value' in val) {
				return val.value === option.value
			}
			return false
		})
	}

	return false
}

const getDisplayLabel = <ValuePropsType extends SelectValueType = string, OptionPropsType extends BaseSelectOption = BaseSelectOption>(
	value: ValuePropsType | null | undefined,
	options: OptionPropsType[],
	labelRender?: (option: OptionPropsType) => React.ReactNode
): React.ReactNode => {
	if (value == null) {
		return null
	}

	if (Array.isArray(value)) {
		const labels = value
			.map((val) => {
				if (typeof val === 'string' || typeof val === 'number') {
					const option = options.find((opt) => opt.value === val)
					return option?.label ?? String(val)
				}
				const labelInValue = val as { label?: string; value?: string }
				const option = options.find((opt) => opt.value === labelInValue.value)
				if (option) {
					return option.label
				}
				return labelInValue.label ?? null
			})
			.filter(Boolean)
		return labels.join(', ')
	}

	if (typeof value === 'object' && 'label' in value) {
		const option = options.find((opt) => opt.value === value.value)
		if (option && labelRender) {
			return labelRender(option)
		}
		return value.label
	}

	const option = options.find((opt) => opt.value === value)
	if (option) {
		return labelRender ? labelRender(option) : option.label
	}

	return String(value)
}

const Select = <ValuePropsType extends SelectValueType = string, OptionPropsType extends BaseSelectOption = BaseSelectOption>(
	props: SelectProps<ValuePropsType, OptionPropsType>
) => {
	const { messages } = useMessages()
	const { formatMessage } = useIntl()

	const {
		label,
		labelScreenReaderOnly,
		id,
		selectStyle = 'default',
		prefixIcon,
		className,
		showSearch = false,
		suffixIcon = <IconRegularChevronDown width='20px' height='20px' color={'icon.tertiary'} />,
		allowClear,
		placeholder,
		mobileSearchPlaceholder = placeholder || messages.Search,
		listHeight = 300,
		menuItemSelectedIcon = <CheckIcon />,
		optionRender,
		options: optionsProps,
		optionFilterProp = 'label',
		filterOption = false,
		onChange,
		value,
		disabled,
		dropdownStyle,
		dropdownMatchSelectWidth,
		onSelect,
		onDeselect,
		onFocus,
		initialLoading = false,
		isRefetching = false,
		isLoadingError = false,
		invalid = false,
		emptyContent,
		notFoundContent,
		labelInValue,
		onSearch,
		onDropdownVisibleChange,
		onClear,
		onClearSearch,
		labelRender,
		getPopupContainer
	} = props

	const { emptyIcon, emptyTitle = messages.Search, emptyLabel = messages['Enter the term you want to search for.'] } = emptyContent || {}

	const generatedId = useId()
	const selectId = id ?? generatedId
	const mobileSearchId = `${selectId}-mobile-search`
	const mobileListboxId = `${selectId}-mobile-listbox`

	const [isOpen, setIsOpen] = useState(false)
	const [isDrawerOpen, setIsDrawerOpen] = useState(false)
	const [searchValue, setSearchValue] = useState('')
	// highlighted index for keyboard navigation in mobile menu
	const [highlightedIndex, setHighlightedIndex] = useState(0)
	const [statusMessage, setStatusMessage] = useState('')

	const selectRef = useRef<BaseSelectRef | null>(null)
	const mobileTriggerRef = useRef<HTMLButtonElement | null>(null)
	const listBoxRef = useRef<HTMLDivElement | null>(null)
	const searchInputRef = useRef<InputRef>(null)

	const isMobile = useIsMobile(BREAKPOINTS_INTS.sm)

	const {
		notFoundIcon,
		notFoundTitle = showSearch && searchValue ? messages['No results found. Try using a different term.'] : messages['Unfortunately, no data was found'],
		notFoundLabel = showSearch && searchValue ? messages['Oops, we didn’t find anything that matches your search.'] : null
	} = notFoundContent || {}

	useEffect(() => {
		if (!isMobile && isDrawerOpen) {
			setIsDrawerOpen(false)
		}
	}, [isMobile, isDrawerOpen])

	const handleFilterOption: SelectProps<ValuePropsType, OptionPropsType>['filterOption'] = (inputValue, option) => {
		if (typeof filterOption === 'function') {
			return filterOption(inputValue, option)
		}

		const compareValue = optionFilterProp === 'label' ? option?.label : option?.value

		return !!transformToLowerCaseWithoutAccent(compareValue)?.includes(transformToLowerCaseWithoutAccent(inputValue))
	}

	const allOptions = optionsProps || []
	const visibleOptions =
		showSearch && filterOption && searchValue
			? allOptions.filter((option) => {
					return handleFilterOption(searchValue, option)
				})
			: allOptions
	// Desktop RcSelect must keep the full list so the selected label stays while typing;
	// the mobile drawer renders `options` itself, so it uses the filtered list.
	const options = isMobile ? visibleOptions : allOptions

	const isInitInitEmptyState = showSearch && !filterOption && !visibleOptions.length && !searchValue
	const isLoading = initialLoading || isRefetching
	const displayLabel = getDisplayLabel(value, options, labelRender)
	const hasValue = value != null && value !== '' && !(Array.isArray(value) && value.length === 0)
	// Plain text label for AT (ignore custom labelRender nodes). rc-select's search input value is
	// empty when showSearch is on, so the visual selection-item is not announced on focus.
	const accessibleValueText = (() => {
		if (!hasValue) {
			return undefined
		}
		const plainLabel = getDisplayLabel(value, options)
		return typeof plainLabel === 'string' && plainLabel.length > 0 ? plainLabel : undefined
	})()
	// Include current value in the accessible name while idle; drop it while the user is typing
	// so the old selection is not re-announced on every keystroke.
	const comboboxAriaLabel = accessibleValueText && !searchValue ? `${label}, ${accessibleValueText}` : undefined
	const isAllowClearEnabled = Boolean(allowClear)
	const showDesktopClear = isAllowClearEnabled && hasValue && !disabled
	const desktopClearIcon = (() => {
		if (typeof allowClear === 'object' && allowClear.clearIcon) {
			return typeof allowClear.clearIcon === 'function' ? allowClear.clearIcon({}) : allowClear.clearIcon
		}
		return <CloseIcon />
	})()

	useEffect(() => {
		const menuOpen = isMobile ? isDrawerOpen : isOpen
		if (!menuOpen) {
			setStatusMessage('')
			return undefined
		}

		const timeoutId = window.setTimeout(() => {
			if (isLoading) {
				setStatusMessage(messages.Loading)
				return
			}

			if (isLoadingError) {
				setStatusMessage(messages['Something went wrong. Data was not loaded.'])
				return
			}

			if (!visibleOptions.length) {
				if (isInitInitEmptyState) {
					setStatusMessage(typeof emptyTitle === 'string' ? emptyTitle : '')
					return
				}
				setStatusMessage(typeof notFoundTitle === 'string' ? notFoundTitle : messages['No results found. Try using a different term.'])
				return
			}

			setStatusMessage(
				formatMessage({ id: RESULTS_AVAILABLE_MESSAGE_ID, defaultMessage: RESULTS_AVAILABLE_MESSAGE_ID }, { count: visibleOptions.length })
			)
		}, STATUS_ANNOUNCE_DEBOUNCE_MS)

		return () => {
			window.clearTimeout(timeoutId)
		}
	}, [
		isMobile,
		isDrawerOpen,
		isOpen,
		isLoading,
		isLoadingError,
		visibleOptions.length,
		isInitInitEmptyState,
		emptyTitle,
		notFoundTitle,
		messages,
		formatMessage
	])

	useEffect(() => {
		if (!isDrawerOpen || highlightedIndex < 0) {
			return
		}

		const optionElement = document.getElementById(`${selectId}-option-${highlightedIndex}`)
		optionElement?.scrollIntoView({ block: 'nearest' })
	}, [highlightedIndex, isDrawerOpen, selectId])

	const handleSetHighlightedIndex = () => {
		// set highlighted index to first selected value or first value in the list
		let firstValueIndex = 0
		let valueToFind: ValuePropsType | null | undefined = value

		if (Array.isArray(value)) {
			valueToFind = value[0] as ValuePropsType
		}

		if (valueToFind) {
			firstValueIndex =
				options.findIndex((option) => {
					if (typeof valueToFind === 'object' && 'value' in valueToFind) {
						return option.value === valueToFind.value
					}
					return option.value === value
				}) ?? 0
		}

		setHighlightedIndex(Math.max(0, firstValueIndex))
	}

	const openMobileDrawer = () => {
		if (disabled) {
			return
		}

		setIsOpen(true)
		setIsDrawerOpen(true)
		onFocus?.()

		setTimeout(() => {
			if (showSearch && searchInputRef.current) {
				searchInputRef.current.focus()
			} else {
				listBoxRef.current?.focus()
				handleSetHighlightedIndex()
			}
		}, 0)
	}

	const handleOnDropdownVisibleChange: RcSelectProps<ValuePropsType, OptionPropsType>['onDropdownVisibleChange'] = (open) => {
		if (onDropdownVisibleChange) {
			onDropdownVisibleChange(open)
		}
		if (!open) {
			setSearchValue('')
		}
		setIsOpen(open)
	}

	const handleChangeDesktop: RcSelectProps<ValuePropsType, OptionPropsType>['onChange'] = (newValue, option) => {
		// When allowClear is enabled, Antd triggers the onChange callback with an undefined value upon clearing.
		// This behavior poses an issue in hook-form-fields, which doesn't recognize undefined as a valid value, resulting in the default value being set instead.
		// More details: https://github.com/orgs/react-hook-form/discussions/5858
		if (onChange) {
			onChange(newValue === undefined ? (null as ValuePropsType) : newValue, option)
		}
	}

	const handleChangeMobile = (isSelected: boolean, currentOption: OptionPropsType) => {
		if (!onChange) {
			return
		}

		let newValue: ValuePropsType | null | undefined = null
		let newOptions: OptionPropsType | OptionPropsType[] = currentOption

		if (Array.isArray(value)) {
			newValue = [...value].reduce((acc) => {
				if (isSelected) {
					return [...acc]
				}

				return [...acc, labelInValue ? { value: currentOption.value, label: currentOption.label } : value]
			}, [])

			newOptions = Array.isArray(newValue)
				? newValue.reduce<OptionPropsType[]>((acc, val) => {
						const option = options.find((opt) => (typeof val === 'object' ? val.value === opt.value : val === opt.value))
						return option ? [...acc, option] : acc
					}, [])
				: []
		}

		if (value && labelInValue) {
			newValue = { value: currentOption.value, label: currentOption.label } as ValuePropsType
		} else {
			newValue = currentOption.value as ValuePropsType
		}
		onChange(newValue ?? null, newOptions)
		setIsDrawerOpen(false)
		setIsOpen(false)
	}

	const handleOnSelectAndDeselect = (option: OptionPropsType, callback: (value: SelectOptionValue<ValuePropsType>, option: OptionPropsType) => void) => {
		return callback((labelInValue ? { value: option.value, label: option.label } : option.value) as SelectOptionValue<ValuePropsType>, option)
	}

	const customDropdownStyle = isInitInitEmptyState
		? {
				minHeight: 0
			}
		: {
				minHeight: 44
			}

	const handleSearch = (newSearchValue: string) => {
		if (onSearch) {
			onSearch(newSearchValue)
		}

		setSearchValue(newSearchValue)
	}

	const handleKeyDownOnMobile = (event: React.KeyboardEvent) => {
		// handles keyboard navigation for mobile menu
		if (!isDrawerOpen || !options.length) return

		const isSelected = value ? getIsSelected<ValuePropsType, OptionPropsType>(value, options[highlightedIndex]) : false

		switch (event.key) {
			case 'ArrowDown':
				event.preventDefault()
				setHighlightedIndex((prevIndex) => (prevIndex + 1) % options.length)
				break
			case 'ArrowUp':
				event.preventDefault()
				setHighlightedIndex((prevIndex) => (prevIndex - 1 + options.length) % options.length)
				break
			case 'Enter':
			case ' ':
				event.preventDefault()
				if (options[highlightedIndex] && onChange) {
					handleChangeMobile(isSelected, options[highlightedIndex])
				}
				break
			case 'Escape':
				setIsOpen(false)
				setIsDrawerOpen(false)
				break
			default:
				break
		}
	}

	const onMobileMenuClose = () => {
		setIsOpen(false)
		setIsDrawerOpen(false)
		mobileTriggerRef.current?.focus()
	}

	const afterMobileMenuOpenChange = (open: boolean) => {
		if (!open) {
			setSearchValue('')
			setHighlightedIndex(-1)
			onClearSearch?.()
		}
		if (onDropdownVisibleChange) {
			onDropdownVisibleChange(open)
		}
	}

	const onMobileMenuItemClick = (option: OptionPropsType, isSelected: boolean) => {
		if (isSelected && onDeselect) {
			handleOnSelectAndDeselect(option, onDeselect)
		}

		if (!isSelected && onSelect) {
			handleOnSelectAndDeselect(option, onSelect)
		}

		if (onChange) {
			handleChangeMobile(isSelected, option)
		}
	}

	const handleMobileClear = () => {
		onChange?.(null)
		onClear?.()
		onClearSearch?.()
		setSearchValue('')
	}

	const handleDesktopClear = (event: React.MouseEvent<HTMLButtonElement>) => {
		event.preventDefault()
		event.stopPropagation()
		onChange?.(null)
		onClear?.()
		onClearSearch?.()
		setSearchValue('')
		setIsOpen(false)
		selectRef.current?.focus()
	}

	const labelRenderWrap: RcSelectProps<ValuePropsType, OptionPropsType>['labelRender'] = labelRender
		? (labelRenderProps) => {
				const valueOption = options.find((option) => option.value === labelRenderProps.value)
				if (valueOption) {
					return labelRender(valueOption)
				}

				return undefined
			}
		: undefined

	const dropdownRender = (menu: React.ReactNode) => {
		const showError = isLoadingError && !isLoading
		const showEmpty = !visibleOptions.length && !isLoading && !isLoadingError

		return (
			<OverlayLoading isLoading={isLoading}>
				{(showError || showEmpty) && (
					<SC.DropdownStateWrapper>
						{showError ? (
							<ErrorScreen />
						) : (
							<Empty
								icon={isInitInitEmptyState ? emptyIcon : notFoundIcon}
								title={isInitInitEmptyState ? emptyTitle : notFoundTitle}
								label={isInitInitEmptyState ? emptyLabel : notFoundLabel}
							/>
						)}
					</SC.DropdownStateWrapper>
				)}
				{/* Keep the listbox mounted so aria-controls / aria-activedescendant stay valid */}
				<SC.DropdownMenuKeeper $hidden={showError || showEmpty}>{menu}</SC.DropdownMenuKeeper>
			</OverlayLoading>
		)
	}

	const statusLiveRegion = (
		<SC.StatusMessage role='status' aria-live='polite' aria-atomic='true'>
			{statusMessage}
		</SC.StatusMessage>
	)

	const mobileTrigger = (
		<InputTrigger
			ref={mobileTriggerRef}
			id={selectId}
			className={className}
			labelName={label}
			value={displayLabel}
			placeholder={placeholder}
			prefixIcon={prefixIcon}
			suffixIcon={suffixIcon as React.ReactNode}
			allowClear={Boolean(allowClear) && hasValue}
			disabled={disabled}
			invalid={invalid}
			fieldStyle={selectStyle}
			expanded={isDrawerOpen}
			ariaLabel={comboboxAriaLabel}
			onClick={openMobileDrawer}
			onFocus={onFocus}
			onClear={handleMobileClear}
		/>
	)

	const desktopSelect = (
		<SC.SelectWrapper>
			{prefixIcon && <SC.PrefixIconWrapper $isDisabled={disabled}>{prefixIcon}</SC.PrefixIconWrapper>}
			<RcSelect<ValuePropsType, OptionPropsType>
				ref={selectRef}
				id={selectId}
				// prevent to have empty string as value, because empty string prevents to show placeholder
				value={value === '' ? null : (value ?? null)}
				className={`${prefixIcon ? 'rc-select-prefix-icon' : ''} ${selectStyle === 'no-borders' ? 'rc-select-no-borders' : ''} ${invalid ? 'rc-select-invalid' : ''} ${isAllowClearEnabled ? 'rc-select-allow-clear' : ''} ${className}`}
				showSearch={showSearch}
				virtual={false}
				suffixIcon={suffixIcon}
				// rc-select clear is a non-focusable span; we render our own clear button for keyboard access
				allowClear={false}
				placeholder={placeholder}
				listHeight={listHeight}
				menuItemSelectedIcon={menuItemSelectedIcon}
				labelInValue={labelInValue}
				aria-label={comboboxAriaLabel}
				optionRender={
					optionRender
						? (option) => {
								return optionRender(option.data)
							}
						: undefined
				}
				options={options}
				labelRender={labelRenderWrap}
				disabled={disabled}
				filterOption={filterOption === false ? false : handleFilterOption}
				optionFilterProp={optionFilterProp}
				dropdownStyle={{ ...customDropdownStyle, ...(dropdownStyle || {}) }}
				dropdownClassName={SELECT_DROPDOWN_CLASS_NAME}
				dropdownMatchSelectWidth={dropdownMatchSelectWidth}
				onSelect={(_, option) => {
					if (onSelect) {
						handleOnSelectAndDeselect(option, onSelect)
					}
				}}
				onDeselect={(_, option) => {
					if (onDeselect) {
						handleOnSelectAndDeselect(option, onDeselect)
					}
				}}
				onFocus={onFocus}
				dropdownRender={dropdownRender}
				open={isOpen}
				onDropdownVisibleChange={handleOnDropdownVisibleChange}
				onChange={handleChangeDesktop}
				onSearch={showSearch ? handleSearch : undefined}
				// not found content is handled in dropdownRender callback with FetchResult component
				// empty <div /> must be returned, otherwise RcSelect would show some default, or doesn't render dropdown at all when null is passed
				notFoundContent={<div />}
				getPopupContainer={getPopupContainer}
			/>
			{showDesktopClear && (
				<SC.ClearButton
					type='button'
					aria-label={formatMessage({ id: 'Clear value for: {labelName}', defaultMessage: 'Clear value for: {labelName}' }, { labelName: label })}
					onClick={handleDesktopClear}
					onMouseDown={(event) => {
						event.preventDefault()
						event.stopPropagation()
					}}
				>
					{desktopClearIcon}
				</SC.ClearButton>
			)}
		</SC.SelectWrapper>
	)

	return (
		<>
			<InputLabel htmlFor={selectId} screenReaderOnly={labelScreenReaderOnly}>
				{label}
			</InputLabel>
			{isMobile ? mobileTrigger : desktopSelect}
			{statusLiveRegion}
			{isMobile && (
				<Drawer
					open={isDrawerOpen}
					mask
					maskClosable
					keyboard
					afterOpenChange={afterMobileMenuOpenChange}
					title={
						showSearch ? (
							<SC.MobileSearchWrapper>
								<InputLabel htmlFor={mobileSearchId} screenReaderOnly>
									{label}
								</InputLabel>
								<SC.MobileSearchInput
									ref={searchInputRef}
									id={mobileSearchId}
									inputMode={'search'}
									role='combobox'
									aria-expanded='true'
									aria-controls={mobileListboxId}
									aria-autocomplete='list'
									value={searchValue ?? null}
									onChange={(e) => {
										handleSearch(e.target.value)
										if (!e.target.value) {
											onClearSearch?.()
										}
									}}
									onClear={() => {
										if (onClearSearch) {
											onClearSearch()
										}
										handleSearch('')
									}}
									placeholder={mobileSearchPlaceholder}
									onKeyDown={(e) => {
										if (e.key === 'ArrowDown') {
											e.preventDefault()
											listBoxRef.current?.focus()
											setHighlightedIndex(0)
										}
									}}
								/>
							</SC.MobileSearchWrapper>
						) : (
							placeholder || label
						)
					}
					onClose={onMobileMenuClose}
					destroyOnClose
				>
					<SC.MobileListWrapper>
						{dropdownRender(
							<SC.MobileListBox
								id={mobileListboxId}
								role='listbox'
								tabIndex={0}
								ref={listBoxRef}
								aria-label={label}
								aria-activedescendant={
									highlightedIndex >= 0 && options[highlightedIndex] ? `${selectId}-option-${highlightedIndex}` : undefined
								}
								onKeyDown={handleKeyDownOnMobile}
							>
								{options.map((option, index) => {
									const isSelected = value ? getIsSelected<ValuePropsType, OptionPropsType>(value, option) : false
									const isHighlighted = index === highlightedIndex

									return (
										<SC.MobileListBoxItem
											key={option.key}
											id={`${selectId}-option-${index}`}
											$isSelected={isSelected}
											$isHighlighted={isHighlighted}
											aria-label={option.label}
											aria-selected={isSelected}
											role='option'
											// Set the highlighted index on onMouseEnter so that when the user hovers over an element with the mouse and then continues navigating with the arrow keys, the navigation will resume from the hovered item's position.
											onMouseEnter={() => setHighlightedIndex(index)}
											onClick={() => onMobileMenuItemClick(option, isSelected)}
										>
											<SC.MobileMenuItemText>{optionRender ? optionRender(option) : option.label}</SC.MobileMenuItemText>
											{isSelected && <SC.MobileMenuItemIconWrapper>{menuItemSelectedIcon}</SC.MobileMenuItemIconWrapper>}
										</SC.MobileListBoxItem>
									)
								})}
							</SC.MobileListBox>
						)}
					</SC.MobileListWrapper>
				</Drawer>
			)}
		</>
	)
}

export default Select
